import { SectionHeader } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { useTheme } from "@emotion/react";
import { formatCurrency } from "@legacy-megarax/react-utils";
import { useContext, useEffect, useRef, useState } from "react";
import { MdExpand, MdExpandLess, MdExpandMore } from "react-icons/md";

import { IamAuthContext } from "@megaron/auth-react";
import { OrderShippingPreview, OrderShippingPreviewInput } from "@megaron/crm-contracts";
import { useClientManager } from "@megaron/react-clients";

type Props = {
  source: OrderShippingPreviewInput;
  refreshKey?: string;
  itemNames?: Record<string, string>;
};

export const OrderShippingSection: React.FC<Props> = (props) => {
  const auth = useContext(IamAuthContext);
  const roles = auth.iamUser?.roles ?? [];
  const hasShippingRole = roles.includes("shipping.user") || roles.includes("shipping.admin");

  if (!hasShippingRole) return null;

  return <OrderShippingPreviewSection {...props} />;
};

const OrderShippingPreviewSection: React.FC<Props> = ({ source, refreshKey, itemNames }) => {
  const shippingPreviewQuery = useClientManager("crm").orderShippingPreview().useQuery(source, {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const previousRefreshKey = useRef(refreshKey);

  useEffect(() => {
    if (previousRefreshKey.current === refreshKey) return;

    previousRefreshKey.current = refreshKey;
    void shippingPreviewQuery.refetch();
  }, [refreshKey, shippingPreviewQuery.refetch]);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
      <SectionHeader isHr>Wysyłka</SectionHeader>
      <QuerySkeleton
        query={shippingPreviewQuery}
        height="220px"
        width="100%"
        onRetry={() => shippingPreviewQuery.refetch()}
        errorScreen={(error) => <ShippingPreviewError error={error} />}
      >
        {(preview) => <ShippingPreview solution={preview.solution} currency={preview.currency} itemNames={itemNames} />}
      </QuerySkeleton>
    </div>
  );
};

const ShippingPreview = ({
  solution,
  currency,
  itemNames = {},
}: Pick<OrderShippingPreview, "solution" | "currency"> & { itemNames?: Record<string, string> }) => {
  const theme = useTheme();
  const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());

  const columns: TableHeadColumn<string>[] = [
    { label: "Jednostka", isSortable: false },
    { label: "Rzeczywiste", isSortable: false, cellCss: { textAlign: "right" } },
    { label: "Koszt", isSortable: false, cellCss: { textAlign: "right" } },
  ];

  const rows = solution.topUnits.flatMap((topUnit, index) => {
    const id = `${topUnit.unit}-${index}`;
    const isExpanded = expandedUnitIds.has(id);
    const displayedCost = isExpanded ? topUnit.topCost : topUnit.cost;
    const unitLabel = topUnit.quantity === 1 ? topUnit.unit : `${topUnit.unit} × ${topUnit.quantity}`;
    const cellsContent: RowCellContent[] = [
      {
        element: (
          <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span>{unitLabel}</span>
            {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
          </div>
        ),
      },
      {
        element: `${sumQuantities(topUnit.unitItems)}/${sumQuantities(topUnit.unitItems) + sumQuantities(topUnit.unitFiller)}`,
        cellCss: { textAlign: "right" },
      },
      {
        element: (
          <span css={{ color: displayedCost.isZero() ? theme.colors.success : theme.colors.danger, fontWeight: 700 }}>
            {formatCurrency(displayedCost, currency)}
          </span>
        ),
        cellCss: { textAlign: "right" },
      },
    ];

    const topUnitRow = {
      id,
      cellsContent,
      onClick: () =>
        setExpandedUnitIds((expandedIds) => {
          const nextIds = new Set(expandedIds);
          if (nextIds.has(id)) nextIds.delete(id);
          else nextIds.add(id);
          return nextIds;
        }),
    };

    if (!isExpanded) return [topUnitRow];

    return [
      topUnitRow,
      ...unitTotalRows(id, topUnit.unitItems, "towar", theme.colors.primary, itemNames),
      ...unitTotalRows(
        id,
        topUnit.unitFiller,
        "wypełnienie",
        theme.colors.secondaryText,
        {},
        topUnit.unitFillerCosts,
        currency,
        theme.colors.danger,
      ),
    ];
  });

  return (
    <div>
      <div css={{ overflow: "auto" }}>
        <Table columns={columns} hideHeader>
          <TableBody rows={rows} />
        </Table>
      </div>
      <div
        css={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          gap: "0.5rem 1.5rem",
          padding: "0.75rem",
        }}
      >
        <strong css={{ color: solution.totalCost.isZero() ? theme.colors.success : theme.colors.danger }}>
          Razem: {formatCurrency(solution.totalCost, currency)}
        </strong>
      </div>
    </div>
  );
};

const unitTotalRows = (
  topUnitId: string,
  quantities: Partial<Record<string, number>>,
  type: string,
  color: string,
  itemNames: Record<string, string> = {},
  costs?: OrderShippingPreview["solution"]["topUnits"][number]["unitFillerCosts"],
  currency?: OrderShippingPreview["currency"],
  costColor?: string,
): { id: string; cellsContent: RowCellContent[] }[] =>
  Object.entries(quantities).map(([unit, quantity]) => {
    const cost = costs?.[unit];

    return {
      id: `${topUnitId}-${type}-${unit}`,
      cellsContent: [
        {
          element: <span css={{ paddingLeft: "1.5rem", color }}>↳ {itemNames[unit] ?? unit}</span>,
        },
        { element: quantity ?? 0, cellCss: { textAlign: "right", color } },
        {
          element: cost && currency && <strong css={{ color: costColor }}>{formatCurrency(cost, currency)}</strong>,
          cellCss: { color, textAlign: "right" },
        },
      ],
    };
  });

const ShippingPreviewError = ({ error }: { error: unknown }) => {
  if (error === "NoSolution") return <>Nie znaleziono konfiguracji wysyłki.</>;
  if (error === "ShippingPriceListNotFound") return <>Nie skonfigurowano cennika wysyłki.</>;

  return <>Nie udało się wyliczyć wysyłki.</>;
};

const sumQuantities = (quantities: Partial<Record<string, number>>) =>
  Object.values(quantities).reduce<number>((total, quantity) => total + (quantity ?? 0), 0);
